import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import type {
  BookingDto,
  CreateBookingInput,
  CreateBookingResult,
  CustomerListResponse,
  DeviceListResponse,
  UpdateBookingInput,
} from '@gc/shared';

import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/features/auth/useAuth';
import {
  createBookingsForDevices,
  timeOfDay,
  type BulkBookingDevice,
  type BulkBookingOutcome,
} from './bookingLogic';

interface BookingDrawerProps {
  /** null = create; otherwise the BOOKED booking being edited. */
  booking: BookingDto | null;
  onClose: () => void;
  onSaved: (warnings: readonly string[]) => void;
}

/** datetime-local wants local wall-clock "YYYY-MM-DDTHH:mm". */
function toLocalInputValue(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** The next round half hour — where a phone booking usually lands. */
function defaultStart(): string {
  const date = new Date();
  date.setSeconds(0, 0);
  date.setMinutes(date.getMinutes() + (30 - (date.getMinutes() % 30)));
  return toLocalInputValue(date);
}

/**
 * Create/edit drawer (WAVE2-DESIGN §1). The party is a walk-in name XOR a
 * registered account — the same rule Tab enforces, driven by the same shared
 * schema the server validates with. On edit, device and party are fixed: the
 * contract's PATCH only moves the window; rebooking another machine is a
 * cancel plus a create, which keeps the audit trail honest.
 *
 * **A booking can cover SEVERAL machines** — a group phoning ahead for five
 * PCs is one party, one hour, five reservations. That used to be reachable only
 * by ticking tiles on the Device List and pressing Reserve there, which was the
 * wrong home for it: on that board "Reserve" now means "these people are here,
 * open the machines". Booking a future hour lives here, next to the calendar
 * that shows it. Picking more than one device loops `POST /bookings` and
 * reports each machine's own answer — a half-booked party is never swallowed.
 */
export function BookingDrawer({ booking, onClose, onSaved }: BookingDrawerProps) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const canSearchCustomers = can('customers.manage');
  const isEdit = booking !== null;

  const [devices, setDevices] = useState<DeviceListResponse['devices']>([]);
  /** Per-device answers after a multi-device run with at least one failure. */
  const [bulkOutcomes, setBulkOutcomes] = useState<BulkBookingOutcome[] | null>(null);
  const [deviceIds, setDeviceIds] = useState<readonly string[]>(
    booking?.deviceId ? [booking.deviceId] : [],
  );
  const [partyMode, setPartyMode] = useState<'walkIn' | 'customer'>(
    booking?.customerAccountId ? 'customer' : 'walkIn',
  );
  const [visitorName, setVisitorName] = useState(booking?.visitorName ?? '');
  const [customerAccountId, setCustomerAccountId] = useState(booking?.customerAccountId ?? '');
  const [customerLabel, setCustomerLabel] = useState(booking?.customerName ?? '');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerOptions, setCustomerOptions] = useState<{ id: string; fullName: string }[]>([]);
  const [startsAtLocal, setStartsAtLocal] = useState(
    booking ? toLocalInputValue(new Date(booking.startsAt)) : defaultStart(),
  );
  const [durationMinutes, setDurationMinutes] = useState(String(booking?.durationMinutes ?? 60));
  const [partySize, setPartySize] = useState(booking?.partySize ? String(booking.partySize) : '');
  const [note, setNote] = useState(booking?.note ?? '');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const response = await api.get<DeviceListResponse>('/devices');
        setDevices(response.devices);
      } catch {
        setDevices([]);
      }
    })();
  }, []);

  /** Chosen machines, in board order, as the bulk creator wants them. */
  const chosen: readonly BulkBookingDevice[] = useMemo(
    () =>
      devices
        .filter((device) => deviceIds.includes(device.id))
        .map((device) => ({ id: device.id, name: device.name })),
    [devices, deviceIds],
  );

  function toggleDevice(id: string): void {
    setDeviceIds((current) =>
      current.includes(id) ? current.filter((kept) => kept !== id) : [...current, id],
    );
  }

  // Debounced account search; only wired when the permission exists — the
  // server would refuse `/customers` anyway, so the field says why instead of
  // failing quietly (AGENTS: no fake UI).
  useEffect(() => {
    if (!canSearchCustomers || partyMode !== 'customer') return;
    const query = customerSearch.trim();
    if (!query) {
      setCustomerOptions([]);
      return;
    }
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const response = await api.get<CustomerListResponse>(
            `/customers?search=${encodeURIComponent(query)}&pageSize=8`,
          );
          setCustomerOptions(response.rows.map(({ id, fullName }) => ({ id, fullName })));
        } catch {
          setCustomerOptions([]);
        }
      })();
    }, 300);
    return () => clearTimeout(timer);
  }, [canSearchCustomers, partyMode, customerSearch]);

  const selectedDevice = useMemo(
    () => (chosen.length === 1 ? (devices.find((d) => d.id === chosen[0]?.id) ?? null) : null),
    [devices, chosen],
  );

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const startsAtIso = new Date(startsAtLocal).toISOString();
      const duration = Number(durationMinutes) || 0;
      const size = partySize ? Number(partySize) : null;
      const trimmedNote = note.trim() || null;

      if (isEdit) {
        const payload: UpdateBookingInput = {
          startsAt: startsAtIso,
          durationMinutes: duration,
          partySize: size,
          note: trimmedNote,
        };
        await api.patch<BookingDto>(`/bookings/${booking.id}`, payload);
        onSaved([]);
      } else if (chosen.length > 1) {
        const result = await createBookingsForDevices(chosen, {
          startsAt: startsAtIso,
          durationMinutes: duration,
          partySize: size,
          note: trimmedNote,
          ...(partyMode === 'customer'
            ? { customerAccountId }
            : { visitorName: visitorName.trim() }),
        });
        // The board reloads for the successes either way; the drawer only
        // closes itself when there is nothing left to read.
        onSaved(result.warnings);
        if (result.failedCount > 0) {
          setBulkOutcomes(result.outcomes);
          return;
        }
      } else {
        // One machine keeps the single-shot route, so the server's own
        // `booking_overlap` refusal can be shown against the field that caused
        // it instead of being flattened into a one-row outcome list.
        const payload: CreateBookingInput = {
          deviceId: chosen[0]?.id ?? '',
          startsAt: startsAtIso,
          durationMinutes: duration,
          partySize: size,
          note: trimmedNote,
          ...(partyMode === 'customer'
            ? { customerAccountId }
            : { visitorName: visitorName.trim() }),
        };
        const result = await api.post<CreateBookingResult>('/bookings', payload);
        onSaved(result.warnings);
      }
      onClose();
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'booking_overlap') {
        const details = caught.details as { startsAt?: string; endsAt?: string } | undefined;
        setError(
          details?.startsAt && details.endsAt
            ? t('bookings.errors.overlap', {
                from: timeOfDay(details.startsAt),
                to: timeOfDay(details.endsAt),
              })
            : caught.message,
        );
      } else {
        setError(caught instanceof ApiError ? caught.message : t('bookings.errors.saveFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const fieldClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-sm text-content outline-none placeholder:text-content-muted focus:border-primary';

  const saveDisabled =
    submitting ||
    !startsAtLocal ||
    !(Number(durationMinutes) >= 15) ||
    (!isEdit &&
      (deviceIds.length === 0 ||
        (partyMode === 'walkIn' ? visitorName.trim() === '' : customerAccountId === '')));

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50"
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
      />

      {/* Pinned to the inline-end edge, so it flips sides under RTL. */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? t('bookings.drawer.editTitle') : t('bookings.drawer.createTitle')}
        className="fixed inset-y-0 z-50 flex w-full max-w-md flex-col border-s border-subtle bg-ground shadow-2xl"
        style={{ insetInlineEnd: 0 }}
      >
        <header className="flex items-center gap-3 border-b border-subtle p-4">
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.cancel')}
            className="rounded-control p-1 text-content-secondary hover:bg-hover hover:text-content"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
          <h2 className="flex-1 truncate text-base font-semibold text-content">
            {isEdit
              ? t('bookings.drawer.editTitle')
              : deviceIds.length > 1
                ? t('bookings.bulk.title', { count: deviceIds.length })
                : t('bookings.drawer.createTitle')}
          </h2>
        </header>

        {bulkOutcomes ? (
          /* At least one device failed: every machine's own answer, per row —
             the successes are already booked, the failures name themselves. */
          <div className="flex min-h-0 flex-1 flex-col overflow-auto p-4">
            <p className="text-sm text-content-secondary">
              {t('bookings.bulk.partial', {
                ok: bulkOutcomes.filter((outcome) => outcome.ok).length,
                failed: bulkOutcomes.filter((outcome) => !outcome.ok).length,
              })}
            </p>
            <ul className="mt-3 space-y-1">
              {bulkOutcomes.map((outcome) => (
                <li
                  key={outcome.deviceId}
                  data-bulk-outcome={outcome.ok ? 'ok' : 'failed'}
                  className="rounded-control bg-raised px-3 py-2 text-xs"
                >
                  <span className="font-medium text-content">{outcome.deviceName}</span>
                  <span className={`ms-2 ${outcome.ok ? 'text-available' : 'text-danger'}`}>
                    {outcome.ok
                      ? t('bookings.bulk.booked')
                      : (outcome.message ?? t('bookings.errors.saveFailed'))}
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              className="mt-6 w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-fg transition-colors hover:bg-primary-hover"
            >
              {t('bookings.bulk.close')}
            </button>
          </div>
        ) : (
          <form
            onSubmit={(event) => void handleSave(event)}
            className="flex min-h-0 flex-1 flex-col overflow-auto p-4"
          >
            {isEdit && booking ? (
              <Field label={t('bookings.drawer.device')}>
                {/* Fixed on edit: the PATCH contract moves the window only. */}
                <select
                  value={booking.deviceId}
                  disabled
                  title={t('bookings.drawer.deviceFixed')}
                  className={fieldClass}
                >
                  <option value={booking.deviceId}>{booking.deviceName}</option>
                </select>
              </Field>
            ) : (
              <Field
                label={
                  deviceIds.length > 1
                    ? t('bookings.bulk.devices', { count: deviceIds.length })
                    : t('bookings.drawer.device')
                }
              >
                {/* Checkboxes, not a multi-select: a group booking five machines
                  is ordinary, and <select multiple> needs a held modifier that
                  no touchscreen at a counter has. */}
                <div
                  data-device-picker
                  className="max-h-40 overflow-auto rounded-control border border-subtle bg-input"
                >
                  {devices.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-content-muted">
                      {t('bookings.drawer.chooseDevice')}
                    </p>
                  ) : (
                    devices.map((device) => (
                      <label
                        key={device.id}
                        className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-content hover:bg-hover"
                      >
                        <input
                          type="checkbox"
                          checked={deviceIds.includes(device.id)}
                          onChange={() => toggleDevice(device.id)}
                          className="h-4 w-4 shrink-0 accent-[var(--brand-primary)]"
                        />
                        <span className="min-w-0 flex-1 truncate">{device.name}</span>
                        {device.currentSession ? (
                          <span className="shrink-0 text-xs text-ending">
                            {t('bookings.drawer.busyNow')}
                          </span>
                        ) : null}
                      </label>
                    ))
                  )}
                </div>
              </Field>
            )}

            {chosen.length === 1 && selectedDevice?.currentSession && !isEdit ? (
              <p className="mt-1 text-xs text-ending">{t('bookings.drawer.deviceBusyHint')}</p>
            ) : null}

            <div className="mt-4 flex overflow-hidden rounded-control border border-subtle bg-raised">
              {(['walkIn', 'customer'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setPartyMode(mode)}
                  disabled={isEdit}
                  aria-pressed={partyMode === mode}
                  className={[
                    'flex-1 px-2 py-2 text-xs transition-colors',
                    partyMode === mode
                      ? 'bg-primary font-medium text-primary-fg'
                      : 'text-content-secondary hover:bg-hover',
                    isEdit ? 'cursor-not-allowed opacity-60' : '',
                  ].join(' ')}
                >
                  {t(`bookings.drawer.${mode}`)}
                </button>
              ))}
            </div>

            <div className="mt-3">
              {partyMode === 'walkIn' ? (
                <Field label={t('bookings.drawer.walkInName')}>
                  <input
                    value={visitorName}
                    onChange={(event) => setVisitorName(event.target.value)}
                    disabled={isEdit}
                    className={fieldClass}
                  />
                </Field>
              ) : canSearchCustomers ? (
                <Field label={t('bookings.drawer.customer')}>
                  {customerAccountId ? (
                    <div className="flex items-center gap-2">
                      <span className="flex-1 truncate rounded-control bg-raised px-3 py-2 text-sm text-content">
                        {customerLabel}
                      </span>
                      {!isEdit ? (
                        <button
                          type="button"
                          onClick={() => {
                            setCustomerAccountId('');
                            setCustomerLabel('');
                          }}
                          className="text-xs text-content-secondary hover:text-content"
                        >
                          {t('bookings.drawer.changeCustomer')}
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    <div className="relative">
                      <Search
                        className="pointer-events-none absolute top-3 h-4 w-4 text-content-muted"
                        style={{ insetInlineStart: '0.6rem' }}
                        aria-hidden
                      />
                      <input
                        value={customerSearch}
                        onChange={(event) => setCustomerSearch(event.target.value)}
                        placeholder={t('bookings.drawer.searchCustomer')}
                        className={`${fieldClass} ps-8`}
                      />
                      {customerOptions.length > 0 ? (
                        <ul className="mt-1 overflow-hidden rounded-control border border-subtle bg-raised">
                          {customerOptions.map((option) => (
                            <li key={option.id}>
                              <button
                                type="button"
                                onClick={() => {
                                  setCustomerAccountId(option.id);
                                  setCustomerLabel(option.fullName);
                                  setCustomerOptions([]);
                                }}
                                className="w-full px-3 py-2 text-start text-sm text-content hover:bg-hover"
                              >
                                {option.fullName}
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  )}
                </Field>
              ) : (
                // Visibly disabled with the reason, never silently broken.
                <p className="rounded-control bg-raised px-3 py-2 text-xs text-content-muted">
                  {t('bookings.drawer.customerSearchNoPermission')}
                </p>
              )}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <Field label={t('bookings.drawer.startsAt')}>
                <input
                  type="datetime-local"
                  value={startsAtLocal}
                  onChange={(event) => setStartsAtLocal(event.target.value)}
                  className={`${fieldClass} tabular`}
                />
              </Field>
              <Field label={t('bookings.drawer.duration')}>
                <input
                  type="number"
                  min="15"
                  max="1440"
                  step="1"
                  value={durationMinutes}
                  onChange={(event) => setDurationMinutes(event.target.value)}
                  className={`${fieldClass} tabular`}
                />
              </Field>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <Field label={t('bookings.drawer.partySize')}>
                <input
                  type="number"
                  min="1"
                  max="64"
                  step="1"
                  value={partySize}
                  onChange={(event) => setPartySize(event.target.value)}
                  className={`${fieldClass} tabular`}
                />
              </Field>
              <Field label={t('bookings.drawer.note')}>
                <input
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  maxLength={300}
                  className={fieldClass}
                />
              </Field>
            </div>

            {error ? (
              <p role="alert" className="mt-3 text-sm text-danger">
                {error}
              </p>
            ) : null}

            <div className="flex-1" />

            <button
              type="submit"
              disabled={saveDisabled}
              className="mt-6 w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-fg transition-colors hover:bg-primary-hover disabled:opacity-60"
            >
              {submitting ? t('common.loading') : t('common.save')}
            </button>
          </form>
        )}
      </aside>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block truncate text-xs text-content-secondary">{label}</span>
      {children}
    </label>
  );
}
